import clsx from "clsx";
import React, { useMemo, useRef } from "react";
import {
  Card,
  CardProps,
  Typography,
  CardMedia,
  CardContent,
  Grid2,
  Button,
  Box,
  IconButton,
  useMediaQuery,
  useTheme,
  Skeleton,
} from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { colorSystem } from "../../theme";

import { useCurrentUser } from "../../hooks/useCurrentUser";
import { JourneyAnchors, UserRole } from "../../constants";
import { ReactComponent as DeleteIcon } from "../../icons/Bin.svg";
import { ReactComponent as ProgressPhotosIcon } from "../../icons/ProgressPhotos.svg";

import { ConfirmActionProgressPhotos } from "./ConfirmActionProgressPhotos";
import { ConfirmActionDialog } from "../dialog/ConfirmActionDialog";
import { WorkoutMediaGallery } from "../dialog/WorkoutMediaGallery";
import AddCircle from "../../icons/AddCircle";
import CompareIcon from "@mui/icons-material/Compare";
import ProgressPhotoService from "../../services/ProgressPhotoService";
import {
  keepPreviousData,
  useQuery,
  useQueryClient,
  useMutation,
  useInfiniteQuery,
} from "@tanstack/react-query";
import { CardPagination } from "../pagination/CardPagination";
import {
  IPaginatedProgressPhotoDto,
  ICreateProgressPhotoSetCommand,
  PaginatedListOfPaginatedProgressPhotoDto,
  PaginatedProgressPhotoDto,
  CreatedSetIds,
} from "@growth-machine-llc/stridist-api-client";
import dayjs from "dayjs";

import { predicateTwoPartQueryKey } from "../../hooks/useOptimisticUpdateMutation";
import { capitalize } from "lodash";
import { useNavigate } from "react-router-dom";
import { PROGRESS_DIALOG_QUERY_KEY } from "../client-progress/ProgressDialog";
import {
  CLIENT_PROGRESS_ROUTE,
  COACH_CLIENT_PROGRESS_ROUTE,
} from "../../routes/routes";
import { useClient } from "../../hooks/useClient";
import ClientDetailsCardHeader from "../coach-clients/ClientDetailsCardHeader";
import { ProfileIconBlock } from "../coach-clients/ProfileIconBlock";
import { useContainerQuery } from "../../hooks/useContainerQuery";

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
    containerType: "inline-size",
    display: "flex",
    flexDirection: "column",
    height: "100%",
  },
  body: {
    display: "flex",
    flexDirection: "column",
    flexGrow: 1,
    justifyContent: "center",

    [theme.containerQueries.up("md")]: {
      display: "block",
    },
  },

  header: {
    display: "flex",
    justifyContent: "end",
    alignItems: "center",
  },

  title: {
    fontSize: 24,
    fontWeight: 600,
    lineHeight: "29px",
    color: theme.palette.common.black,
  },

  subtitle: {
    fontSize: 14,
    fontWeight: 500,
    lineHeight: "20px",
    color: theme.palette.text.secondary,
  },

  button: {
    color: theme.palette.primary.main,
    backgroundColor: "transparent",
    boxShadow: "none",
    height: theme.spacing(4),
    fontSize: 14,
    fontWeight: 600,
    textWrap: "nowrap",
    margin: theme.spacing(1),
    [theme.containerQueries.up("md")]: {
      marginX: theme.spacing(4),
    },
  },

  noText: {
    fontSize: 16,
    color: theme.palette.text.secondary,
    margin: theme.spacing(4),
    textAlign: "center",
  },

  image: {
    height: 264,
    cursor: "pointer",
  },

  content: {
    padding: `${theme.spacing(0.75, 1.5)} !important`,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    backgroundColor: colorSystem.blue5,
  },

  date: {
    fontSize: 16,
    fontWeight: 500,
    lineHeight: "26px",
    color: theme.palette.common.black,
  },

  label: {
    fontSize: 16,
    fontWeight: 500,
    lineHeight: "20px",
    color: theme.palette.text.secondary,
  },
  pagination: {
    width: "100%",
    [theme.containerQueries.down("md")]: {
      marginTop: "auto",
    },
  },
  paginationItem: {
    "& .Mui-selected:hover": {
      backgroundColor: "transparent",
      color: theme.palette.primary.main,
      borderWidth: 1,
      borderStyle: "solid",
      borderColor: theme.palette.primary.main,
    },
    "& .Mui-selected": {
      backgroundColor: "transparent",
      color: theme.palette.primary.main,
      borderWidth: 1,
      borderStyle: "solid",
      borderColor: theme.palette.primary.main,
    },
  },
  imageContainer: {
    marginTop: 0,
    display: "flex",
    flexDirection: "row",
    flexWrap: "wrap",
    [theme.containerQueries.down("sm")]: {
      flexWrap: "nowrap",
      overflow: "scroll",
      paddingBottom: theme.spacing(2),
    },
  },
  arrowIconDown: {
    transform: "rotate(180deg)",
  },
  images: {
    [theme.containerQueries.down("sm")]: {
      minWidth: "70%",
    },
  },
  actions: {
    position: "absolute",
    right: 0,
    display: "flex",
    flexDirection: "column",
    marginTop: theme.spacing(1),
    [theme.containerQueries.up("md")]: {
      flexDirection: "row",
      marginTop: 0,
    },
  },
  headerContainer: {
    display: "flex",
    alignItems: "center",
    width: "100%",
  },
  icon: {
    marginRight: theme.spacing(2),
    marginTop: theme.spacing(0.5),
  },
  openDialogButton: {
    marginLeft: "auto",
  },

  bottomActions: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-around",
    marginTop: theme.spacing(4),
  },
}));

const ProgressPhotoSkeleton = () => {
  const s = useStyles();
  return (
    <>
      <Card elevation={0} variant="outlined">
        <CardMedia className={s.image} />
        <Box
          sx={{
            padding: (theme) => theme.spacing(1.1, 1.5, 0.75, 1.5),
          }}
        >
          <Skeleton
            animation="wave"
            variant="rounded"
            height={16}
            width="38%"
          />
        </Box>
        <Box
          sx={{
            padding: (theme) => theme.spacing(0.1, 1.5, 1.75, 1.5),
          }}
        >
          <Skeleton
            animation="wave"
            variant="rounded"
            height={16}
            width="14%"
          />
        </Box>
      </Card>
    </>
  );
};

export interface ClientProgressPhotosCardProps extends CardProps {
  clientId: number;
  isPreview?: boolean;
  collapsable?: boolean;
  defaultOpen?: boolean;
}

export const CLIENT_PROGRESS_PHOTOS_PAGE_QUERY_KEY =
  "client-progressphotos-page";
const CLIENT_PROGRESS_PHOTOS_PAGE_SIZE = 6;

export function ClientProgressPhotosCard(props: ClientProgressPhotosCardProps) {
  const { className, clientId, isPreview, collapsable, defaultOpen, ...other } =
    props;
  const s = useStyles();
  const containerRef = useRef<HTMLDivElement>(null);
  const queryClient = useQueryClient();
  const user = useCurrentUser();
  const clientData = useClient();
  const id = clientId;
  const [currentPage, setCurrentPage] = React.useState(1);

  const navigate = useNavigate();

  const smUp = useContainerQuery(containerRef, (theme) =>
    theme.containerQueries.up("sm"),
  );
  const {
    data,
    isFetching,
    isLoading: isFirstPageFirstFetch,
    isPlaceholderData: isPrevPageData,
  } = useQuery({
    queryKey: [
      CLIENT_PROGRESS_PHOTOS_PAGE_QUERY_KEY,
      { clientId: id, currentPage },
    ],
    queryFn: () =>
      ProgressPhotoService.getPhotos(
        currentPage,
        CLIENT_PROGRESS_PHOTOS_PAGE_SIZE,
        id,
      ),
    placeholderData: keepPreviousData,
    select: (data) => ({
      ...data,
      items: data.items.map((p) => ({
        ...p,
        takenOn: dayjs(p.takenOn).format("MMM DD, YYYY"),
      })),
    }),
  });

  const firstPage: PaginatedListOfPaginatedProgressPhotoDto =
    queryClient.getQueryData([
      CLIENT_PROGRESS_PHOTOS_PAGE_QUERY_KEY,
      { clientId: id, currentPage: 1 },
    ]);

  const lastRecorded = firstPage?.items[0]?.takenOn;

  const photosList = data?.items ?? [];

  const { mutate: deletePhotoFromList, isPending: deleting } = useMutation({
    mutationKey: ["delete-signle-progress-photo"],
    mutationFn: ProgressPhotoService.deleteSinglePhoto,
    onSuccess: () => {
      // invalidate all pages after the current page - no refetch
      queryClient.invalidateQueries({
        predicate: (query) =>
          predicateTwoPartQueryKey(
            query.queryKey,
            CLIENT_PROGRESS_PHOTOS_PAGE_QUERY_KEY,
            (params) => {
              return (
                params?.clientId === id && params?.currentPage > currentPage
              );
            },
          ),
        queryKey: [CLIENT_PROGRESS_PHOTOS_PAGE_QUERY_KEY],
        refetchType: "none",
      });

      // invalidate current page - with refetch
      queryClient.invalidateQueries({
        queryKey: [
          CLIENT_PROGRESS_PHOTOS_PAGE_QUERY_KEY,
          { clientId: id, currentPage },
        ],
      });

      // invalidate progress dialog
      queryClient.invalidateQueries({
        queryKey: [PROGRESS_DIALOG_QUERY_KEY, { clientId: id }],
        refetchType: "none",
      });
    },
  });

  const [visibleAction, setVisibleAction] = React.useState(false);
  const [visibleActionDelete, setVisibleActionDelete] = React.useState(false);
  const [chooseImage, setChooseImage] = React.useState<number>(null);
  const [selectedMediaIndex, setSelectedMediaIndex] = React.useState(0);
  const [openMedia, setOpenMedia] = React.useState(false);
  const [isOpenContent, setIsOpenContent] = React.useState(true);

  const updatePhotosList = React.useCallback(
    (ids: CreatedSetIds, vars: ICreateProgressPhotoSetCommand) => {
      // construct the list of new photos
      const queryUpdateInterface: IPaginatedProgressPhotoDto[] =
        vars.photos.map((p) => ({
          id: ids.photoIds[p.title],
          title: p.title,
          url: p.url,
          fileName: p.fileName,
          size: p.size,
          progressPhotoSetId: ids.setId,
          takenOn: dayjs().format("MMM DD, YYYY"),
        }));
      const queryUpdateData: PaginatedProgressPhotoDto[] =
        queryUpdateInterface.map((i) => PaginatedProgressPhotoDto.fromJS(i));

      // upsert the new photos into the first page
      queryClient.setQueryData(
        [CLIENT_PROGRESS_PHOTOS_PAGE_QUERY_KEY, { clientId, currentPage: 1 }],
        (
          prev: PaginatedListOfPaginatedProgressPhotoDto,
        ): PaginatedListOfPaginatedProgressPhotoDto => {
          const overflow =
            prev.items.length +
            queryUpdateData.length -
            CLIENT_PROGRESS_PHOTOS_PAGE_SIZE;
          const remains = prev.items.slice(
            0,
            overflow > 0 ? -overflow : prev.items.length,
          );
          return PaginatedListOfPaginatedProgressPhotoDto.fromJS({
            ...prev,
            hasNextPage: Boolean(overflow),
            totalPages: prev.totalPages + (overflow ? 1 : 0),
            items: [...queryUpdateData, ...remains],
          });
        },
      );

      // redirect to the first page
      setCurrentPage(1);

      // invalidate the cache for all pages without refetching
      queryClient.invalidateQueries({
        queryKey: [CLIENT_PROGRESS_PHOTOS_PAGE_QUERY_KEY],
        refetchType: "none",
      });

      // invalidate progress dialog
      queryClient.invalidateQueries({
        queryKey: [PROGRESS_DIALOG_QUERY_KEY, { clientId: id }],
        refetchType: "none",
      });
    },
    [queryClient, clientId],
  );

  const theme = useTheme();

  const handleRemove = React.useCallback(() => {
    deletePhotoFromList(chooseImage, {
      onSuccess: () => {
        setVisibleActionDelete(false);
      },
    });
  }, [deletePhotoFromList, chooseImage]);

  const handleRemoveAction = React.useCallback(
    (id: number) => {
      setVisibleActionDelete(true);
      setChooseImage(id);
    },
    [setChooseImage],
  );

  const handleMediaClick = React.useCallback(
    (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
      event.preventDefault();
      event.stopPropagation();

      const {
        dataset: { mediaIndex },
      } = event.currentTarget;

      setOpenMedia(true);
      setSelectedMediaIndex(parseInt(mediaIndex));
    },
    [],
  );

  const handleMediaDialogClose = React.useCallback(() => {
    setOpenMedia(false);
  }, []);

  // TODO_API_V2_TYPES: rename fields to match API and drop this mapping
  const galleryImages = useMemo(
    () =>
      photosList.map((photo) => ({
        url: photo.url,
        name: photo.title,
        createdAt: photo.takenOn,
        label: photo.title,
        idProgram: photo.progressPhotoSetId.toString(),
      })),
    [photosList],
  );

  const showCards = photosList?.length && !isPrevPageData;
  const showSkeleton = isFirstPageFirstFetch || (isFetching && isPrevPageData);

  const ProgressPhotoCardActions = () => (
    <>
      <Button
        className={s.button}
        startIcon={<AddCircle fill={theme.palette.primary.main} />}
        children="Add new photo"
        onClick={() => setVisibleAction(true)}
      />
      <Button
        className={s.button}
        startIcon={<CompareIcon sx={{ color: theme.palette.primary.main }} />}
        children="Compare"
        onClick={() => {
          navigate(
            (clientData
              ? COACH_CLIENT_PROGRESS_ROUTE
              : CLIENT_PROGRESS_ROUTE
            ).replace(":username", clientData?.username || user?.username),
          );
        }}
      />
    </>
  );

  return (
    <Card
      ref={containerRef}
      className={clsx(s.root, className, JourneyAnchors.CLIENT_PROGRESS_PHOTOS)}
      elevation={0}
      variant="outlined"
      {...other}
    >
      <ClientDetailsCardHeader
        icon={
          <ProfileIconBlock icon={<ProgressPhotosIcon />} bgVariant="accent" />
        }
        title="Progress photos"
        subtitle={
          lastRecorded &&
          `Last recorded ${dayjs(lastRecorded).format("MMM DD, YYYY")}`
        }
        isOpenContent={isOpenContent}
        setIsOpenContent={
          user.role !== UserRole.COACH ? setIsOpenContent : undefined
        }
        isPreview={isPreview}
        collapsable={collapsable}
        defaultOpen={defaultOpen}
      >
        {smUp && (
          <Box className={s.actions}>
            <ProgressPhotoCardActions />
          </Box>
        )}
      </ClientDetailsCardHeader>
      {isOpenContent && (
        <Box className={s.body}>
          {showCards ? (
            <Grid2 container spacing={2} className={s.imageContainer}>
              {photosList.map(
                ({ id, url, title, takenOn, progressPhotoSetId }, index) => (
                  <>
                    <Grid2
                      key={url}
                      className={s.images}
                      size={{
                        xs: 12,
                        sm: 6,
                        md: 4,
                      }}
                    >
                      <Card elevation={0}>
                        <CardMedia
                          className={s.image}
                          image={url}
                          title={`${capitalize(title)}, ${takenOn}`}
                          onClick={handleMediaClick}
                          data-media-index={index}
                        />
                        <CardContent className={s.content}>
                          <Box>
                            <Typography
                              className={s.date}
                              variant="body1"
                              children={takenOn}
                            />
                            <Typography
                              className={s.label}
                              variant="body1"
                              children={capitalize(title)}
                            />
                          </Box>
                          {user.role !== UserRole.COACH && (
                            <IconButton
                              children={
                                <DeleteIcon
                                  width={26}
                                  height={26}
                                  color={theme.palette.text.secondary}
                                />
                              }
                              onClick={() => handleRemoveAction(id)}
                              disabled={false}
                              size="large"
                            />
                          )}
                        </CardContent>
                      </Card>
                    </Grid2>
                  </>
                ),
              )}
            </Grid2>
          ) : showSkeleton ? (
            <Grid2 container spacing={2} className={s.imageContainer}>
              {Array.from({ length: CLIENT_PROGRESS_PHOTOS_PAGE_SIZE }).map(
                (_, i) => (
                  <Grid2
                    key={i}
                    className={s.images}
                    size={{
                      xs: 12,
                      sm: 6,
                      md: 4,
                    }}
                  >
                    <ProgressPhotoSkeleton />
                  </Grid2>
                ),
              )}
            </Grid2>
          ) : (
            <Typography className={s.noText}>
              No progress photos to show.
            </Typography>
          )}
          {data?.totalPages > 1 && (
            <CardPagination
              page={currentPage}
              className={s.pagination}
              count={data?.totalPages}
              onChange={(_, page) => setCurrentPage(page)}
            />
          )}

          {!smUp && (
            <Box className={s.bottomActions}>
              <ProgressPhotoCardActions />
            </Box>
          )}
        </Box>
      )}
      <ConfirmActionProgressPhotos
        title={"Progress photos"}
        onCancel={() => setVisibleAction(false)}
        onConfirm={() => {}}
        open={visibleAction}
        id={id}
        updatePhotosList={updatePhotosList}
      />
      <ConfirmActionDialog
        title="Are you sure you want to remove this result?"
        onCancel={() => setVisibleActionDelete(false)}
        onConfirm={handleRemove}
        open={visibleActionDelete}
        disabled={deleting}
      />
      {openMedia && (
        <WorkoutMediaGallery
          open
          defaultIndex={selectedMediaIndex}
          onClose={handleMediaDialogClose}
          images={galleryImages}
        />
      )}
    </Card>
  );
}
